import React, { useEffect, useState } from 'react'
import type { TabValue, PainterScheduleEvent } from './types'
import type { usePainterWorkOrderStyles } from './usePainterWorkOrderStyles'
import NotesDocList from 'src/components/common/NotesDocList'
import DealTaskDocList from 'src/components/common/TaskTable/DealTaskDocList'
import DealPaymentsTable from 'src/components/common/DealPaymentsTable'
import FilesDocList from 'src/components/common/QuoteTable/FilesDocList'
import type { QuoteFile } from 'paintscout'
import type { Task } from '@paintscout/api'
import { DealDocument } from '@paintscout/api'
import { Activity, useDialogs, useUser } from '@paintscout/ui-business'
import { useNoteMutations, useDealMutations } from 'shared/hooks'
import { TaskDialog } from 'src/components/dialogs'
import striptags from 'striptags'
import { useApolloClient } from '@apollo/react-hooks'

interface JobTabContentProps {
  event: PainterScheduleEvent
  activeTab: Exclude<TabValue, 'workOrder'>
  classes: ReturnType<typeof usePainterWorkOrderStyles>
  onAddPayment?: () => void
  pendingNewTask?: Task | null
  onPendingNewTaskConsumed?: () => void
}

export default function JobTabContent({
  event,
  activeTab,
  classes,
  onAddPayment,
  pendingNewTask,
  onPendingNewTaskConsumed
}: JobTabContentProps) {
  switch (activeTab) {
    case 'payments':
      return <PaymentsTab event={event} classes={classes} onAddPayment={onAddPayment} />
    case 'tasks':
      return (
        <TasksTab
          event={event}
          classes={classes}
          pendingNewTask={pendingNewTask}
          onPendingNewTaskConsumed={onPendingNewTaskConsumed}
        />
      )
    case 'notes':
      return <NotesTab event={event} classes={classes} />
    case 'files':
      return <FilesTab event={event} classes={classes} />
    default:
      return null
  }
}

function PaymentsTab({ event, onAddPayment }: Omit<JobTabContentProps, 'activeTab'>) {
  return (
    <div style={{ margin: '-16px' }}>
      <DealPaymentsTable
        relatedDocs={event.sourceQuote ? [event.sourceQuote] : []}
        readonly
        hideTitle
        addClickHandler={onAddPayment}
      />
    </div>
  )
}

function TasksTab({ event, pendingNewTask, onPendingNewTaskConsumed }: Omit<JobTabContentProps, 'activeTab'>) {
  const [refreshKey, setRefreshKey] = useState(0)
  const { openDialog, dismissDialog } = useDialogs()
  const { isPainter } = useUser()

  useEffect(() => {
    if (!pendingNewTask) return
    setRefreshKey((k) => k + 1)
    onPendingNewTaskConsumed?.()
  }, [pendingNewTask]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleNewClick({ type }: { type: string; key?: string }) {
    if (type !== 'task') return
    openDialog(TaskDialog, {
      dealId: event.dealId,
      onConfirm: () => {
        setRefreshKey((k) => k + 1)
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  return (
    <div style={{ margin: '-16px' }}>
      <DealTaskDocList
        key={refreshKey}
        dealId={event.dealId}
        taskTemplateOrder={[]}
        disableChecklistActions={isPainter}
        onNewClick={handleNewClick}
        onReorderTaskTemplates={() => {}}
      />
    </div>
  )
}

function NotesTab({ event }: Omit<JobTabContentProps, 'activeTab'>) {
  const notesQuery = `docIds:"${event.dealId}" AND details.activityType:note`
  const { createNote } = useNoteMutations()
  const { saveDeal } = useDealMutations()
  const client = useApolloClient()

  async function handleSaveNote(files: QuoteFile[], note: string) {
    const hasNote = striptags(note).trim().length > 0
    if (!hasNote && files.length === 0) return

    if (files.length > 0 && event.dealId) {
      const result = await client.query({
        query: DealDocument,
        variables: { id: event.dealId },
        fetchPolicy: 'network-only'
      })
      const deal = result.data?.deal
      if (deal) {
        const existingFiles = Object.values(deal.files ?? {}) as QuoteFile[]
        const existingKeys = new Set(existingFiles.map((f: QuoteFile) => f.key))
        const newFiles = files.filter((f) => !existingKeys.has(f.key))
        if (newFiles.length > 0) {
          await saveDeal({ deal: { ...deal, files: [...existingFiles, ...newFiles] } })
        }
      }
    }

    await createNote({
      docId: event.dealId,
      docType: 'deal',
      note,
      files: files.map((f) => f.key)
    })
  }

  function handleNoteFileDetails(fileIdString: string): QuoteFile {
    const fileId = fileIdString.split('_')[1]
    const files = (event.sourceQuote?.files ?? {}) as Record<string, QuoteFile>
    return files[fileId] ?? ({} as QuoteFile)
  }

  return (
    <div style={{ margin: '-16px' }}>
      <NotesDocList
        docId={event.dealId}
        query={notesQuery}
        ActivityComponent={Activity}
        onSaveNote={handleSaveNote}
        onNoteFileDetails={handleNoteFileDetails}
      />
    </div>
  )
}

function FilesTab({ event }: Omit<JobTabContentProps, 'activeTab'>) {
  const files = (event.dealFiles ?? []) as QuoteFile[]
  const client = useApolloClient()
  const { saveDeal } = useDealMutations()

  async function handleUpload(newFiles: QuoteFile[]) {
    if (!event.dealId) return
    const result = await client.query({
      query: DealDocument,
      variables: { id: event.dealId },
      fetchPolicy: 'network-only'
    })
    const deal = result.data?.deal
    if (!deal) return
    await saveDeal({ deal: { ...deal, files: newFiles }, delayedRefetchQueries: ['painterSchedule'] })
  }

  return (
    <div style={{ margin: '-16px' }}>
      <FilesDocList docId={event.dealId} files={files} onUpload={handleUpload} />
    </div>
  )
}
